import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Row, Col, Image, Typography, Input, Select, Button, Upload, message, Space, Tag, Empty, Modal, Progress, Checkbox, Popconfirm, DatePicker, Segmented, Pagination } from 'antd';
import { DownloadOutlined, UploadOutlined, SearchOutlined, PictureOutlined, DeleteOutlined, CalendarOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';
import { hasPerm } from '../utils/perms';

// Download allowed for privileged roles, or any user granted the permission.
const canDownloadThumb = (user) =>
  ['admin', 'manager', 'graphic_designer'].includes(user?.role) || hasPerm(user, 'thumbnail_download');

const { RangePicker } = DatePicker;

const { Title, Text } = Typography;
const { Option } = Select;

const API_BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api$/, '') : '';

const CATEGORIES = [
  { value: 'music', label: 'Music Thumbnail', color: 'magenta' },
  { value: 'distribution', label: 'Distribution Thumbnail', color: 'blue' },
  { value: 'new_song_distribution', label: 'New Song Distribution', color: 'geekblue' },
  { value: 'bhajan', label: 'Bhajan Thumbnail', color: 'orange' },
  { value: 'channel_logo', label: 'Channel Logo', color: 'green' },
  { value: 'podcast', label: 'Podcast Thumbnail', color: 'purple' },
  { value: 'channel_art', label: 'Channel Art Cover', color: 'cyan' },
];

const catMeta = (v) => CATEGORIES.find(c => c.value === v) || { label: v, color: 'default' };

export default function Thumbnails({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 60;
  const [uploadCategory, setUploadCategory] = useState('music');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [uploadDate, setUploadDate] = useState(dayjs()); // event date for new thumbnails (weekly grouping)
  const [dateRange, setDateRange] = useState(null);       // [from, to] dayjs for weekly download filter
  const [editingThumbId, setEditingThumbId] = useState(null);
  const [editThumbTitle, setEditThumbTitle] = useState('');

  // Guards against the duplicate-upload bug: the antd Upload's onChange can fire
  // multiple times for a single multi-file selection. We collect the files, debounce,
  // and only ever run ONE upload request at a time.
  const uploadingRef = useRef(false);
  const pendingFilesRef = useRef([]);
  const uploadTimerRef = useRef(null);

  // Thumbnail UPLOAD: admin + graphic_designer, or anyone granted 'thumbnail_upload'
  const canUpload = ['admin', 'graphic_designer'].includes(user?.role) || hasPerm(user, 'thumbnail_upload');
  // Thumbnail DOWNLOAD: privileged roles, or any user granted the 'thumbnail_download' permission
  const canDownload = canDownloadThumb(user);

  const fromStr = dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : '';
  const toStr = dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : '';

  const handleThumbTitleEdit = async (id) => {
    if (!editThumbTitle.trim()) { message.warning('Title cannot be empty'); return; }
    try {
      await api.put(`/thumbnails/${id}`, { title: editThumbTitle.trim() });
      message.success('Title updated');
      setEditingThumbId(null);
      fetchItems();
    } catch (err) { message.error('Failed to update title'); }
  };

  const fetchItems = useCallback(async (p) => {
    const pg = p || page;
    setLoading(true);
    try {
      const res = await api.get('/thumbnails', { params: { q: search, category: catFilter, from: fromStr, to: toStr, page: pg, limit: PAGE_SIZE } });
      const data = res.data;
      if (data && Array.isArray(data.items)) {
        setItems(data.items);
        setTotal(data.total || 0);
      } else if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [search, catFilter, fromStr, toStr, page]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 300); // debounce search
    return () => clearTimeout(t);
  }, [fetchItems]);

  const handleBulkUpload = async (fileList) => {
    // Hard guard: never run two upload batches at once (prevents 2×/4× duplicates).
    if (uploadingRef.current) return;
    // De-duplicate files within the batch by name+size (a selection can list a file twice).
    const seen = new Set();
    const files = [];
    fileList.forEach(f => {
      const raw = f.originFileObj || f;
      const key = `${raw.name}_${raw.size}`;
      if (seen.has(key)) return;
      seen.add(key);
      files.push(raw);
    });
    if (files.length === 0) return;

    uploadingRef.current = true;
    setUploading(true); setUploadProgress(0);
    // Upload in small batches so any number of files works (the server caps files
    // per single request at max_file_uploads). 100-200+ files upload reliably.
    const BATCH = 40;
    const eventDate = (uploadDate || dayjs()).format('YYYY-MM-DD');
    let totalUploaded = 0;
    const allSkipped = [];
    let hadError = false;
    try {
      for (let start = 0; start < files.length; start += BATCH) {
        const chunk = files.slice(start, start + BATCH);
        const formData = new FormData();
        chunk.forEach(f => formData.append('files[]', f));
        formData.append('category', uploadCategory);
        formData.append('event_date', eventDate);
        try {
          const res = await api.post('/thumbnails/upload', formData, {
            onUploadProgress: (e) => {
              if (e.total) {
                const batchPct = (e.loaded * 100) / e.total;
                const overall = ((start + (chunk.length * batchPct / 100)) / files.length) * 100;
                setUploadProgress(Math.min(99, Math.round(overall)));
              }
            }
          });
          totalUploaded += res.data.uploaded || 0;
          if (res.data.skipped && res.data.skipped.length) allSkipped.push(...res.data.skipped);
          if (res.data.distribution_matched && res.data.distribution_matched.length) {
            message.info(`${res.data.distribution_matched.length} song(s) marked as Distributed`, 6);
          }
          if (res.data.unmatched && res.data.unmatched.length) {
            Modal.warning({
              title: 'No Matching Song Found',
              content: `These thumbnails did not match any song title: ${res.data.unmatched.slice(0,10).join(', ')}${res.data.unmatched.length > 10 ? ` …and ${res.data.unmatched.length - 10} more` : ''}. Please check the file names match your song titles.`,
              okText: 'OK',
            });
          }
        } catch (err) {
          // A 400 here means the whole batch was duplicates/invalid — record and continue.
          const data = err.response?.data;
          if (data && Array.isArray(data.skipped)) { allSkipped.push(...data.skipped); }
          else { hadError = true; allSkipped.push(`${chunk.length} file(s): ${err.response?.data?.error || 'upload failed'}`); }
        }
      }
      setUploadProgress(100);
      const nSkip = allSkipped.length;
      if (totalUploaded > 0) {
        message.success(`${totalUploaded} new thumbnail(s) uploaded${nSkip ? `, ${nSkip} already existed/skipped` : ''}`);
      }
      if (nSkip && !totalUploaded && !hadError) {
        Modal.warning({
          title: 'Already Exists',
          content: `All ${nSkip} file(s) already exist and were skipped: ${allSkipped.slice(0, 10).join(', ')}${nSkip > 10 ? ` …and ${nSkip - 10} more` : ''}`,
          okText: 'OK',
        });
      } else if (hadError && !totalUploaded) {
        Modal.error({
          title: 'Upload Failed',
          content: `Upload failed for some files: ${allSkipped.slice(0, 10).join(', ')}`,
          okText: 'OK',
        });
      } else if (nSkip && totalUploaded) {
        Modal.info({
          title: 'Some files skipped',
          content: `${nSkip} file(s) already existed: ${allSkipped.slice(0, 10).join(', ')}${nSkip > 10 ? ` …and ${nSkip - 10} more` : ''}`,
          okText: 'OK',
        });
      }
      setPage(1);
      fetchItems(1);
    } finally {
      setUploading(false);
      uploadingRef.current = false;
    }
  };

  // antd Upload can call onChange several times for one multi-select; collect the
  // files and debounce so exactly one upload request runs per selection.
  const onUploadChange = (info) => {
    if (uploadingRef.current) return;
    pendingFilesRef.current = info.fileList || [];
    if (uploadTimerRef.current) clearTimeout(uploadTimerRef.current);
    uploadTimerRef.current = setTimeout(() => {
      const files = pendingFilesRef.current;
      pendingFilesRef.current = [];
      if (files.length) handleBulkUpload(files);
    }, 350);
  };

  const setThisWeek = () => {
    // Monday to Saturday of the current week
    const monday = dayjs().startOf('week').add(1, 'day');
    const saturday = monday.add(5, 'day');
    setDateRange([monday, saturday]);
  };

  const downloadWeek = () => {
    if (!fromStr || !toStr) { message.info('Please select a week/date range first'); return; }
    const token = localStorage.getItem('token');
    const cat = catFilter ? `&category=${catFilter}` : '';
    window.open(`${API_BASE}/api/thumbnails/download-zip?from=${fromStr}&to=${toStr}${cat}&token=${token}`, '_blank');
  };

  const downloadOne = (id) => {
    const token = localStorage.getItem('token');
    window.open(`${API_BASE}/api/thumbnails/${id}/download?token=${token}`, '_blank');
  };

  const downloadSelected = () => {
    if (selected.length === 0) { message.info('Please select thumbnails first'); return; }
    const token = localStorage.getItem('token');
    window.open(`${API_BASE}/api/thumbnails/download-zip?ids=${selected.join(',')}&token=${token}`, '_blank');
  };

  const downloadAllShown = () => {
    if (items.length === 0) return;
    const token = localStorage.getItem('token');
    const ids = items.map(i => i.id).join(',');
    window.open(`${API_BASE}/api/thumbnails/download-zip?ids=${ids}&token=${token}`, '_blank');
  };

  const deleteOne = async (id) => {
    try {
      await api.delete(`/thumbnails/${id}`);
      message.success('Deleted');
      setSelected(s => s.filter(x => x !== id));
      fetchItems();
    } catch (err) { message.error(err.response?.data?.error || 'Delete failed'); }
  };

  const deleteSelected = async () => {
    if (selected.length === 0) { message.info('Please select thumbnails first'); return; }
    try {
      const res = await api.post('/thumbnails/bulk-delete', { ids: selected });
      message.success(res.data?.message || 'Deleted');
      setSelected([]);
      fetchItems();
    } catch (err) { message.error(err.response?.data?.error || 'Bulk delete failed'); }
  };

  const deleteAll = async () => {
    try {
      const res = await api.post('/thumbnails/delete-all');
      message.success(res.data?.message || 'All thumbnails deleted');
      setSelected([]);
      setPage(1);
      fetchItems(1);
    } catch (err) { message.error(err.response?.data?.error || 'Delete all failed'); }
  };

  const deleteDateRange = async (days) => {
    const cat = uploadCategory || catFilter || 'distribution';
    const label = days === 0 ? 'today' : `last ${days + 1} days`;
    try {
      const res = await api.delete('/thumbnails/date-range', { data: { days, category: cat } });
      message.success(res.data?.message || `Deleted ${label} thumbnails`);
      setSelected([]);
      setPage(1);
      fetchItems(1);
    } catch (err) { message.error(err.response?.data?.error || 'Date range delete failed'); }
  };

  const toggleSelect = (id, checked) => {
    setSelected(s => checked ? [...s, id] : s.filter(x => x !== id));
  };

  const allShownSelected = items.length > 0 && items.every(i => selected.includes(i.id));
  const toggleSelectAll = (checked) => {
    setSelected(checked ? items.map(i => i.id) : []);
  };

  return (
    <div>
      <Modal open={uploading} footer={null} closable={false} maskClosable={false} title="Uploading..." width={400}>
        <Progress percent={uploadProgress} status={uploadProgress < 100 ? 'active' : 'success'} />
        <Text type="secondary">{uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : 'Processing on server...'}</Text>
      </Modal>

      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap' }}>
        <Title level={3} style={{ margin: 0 }}><PictureOutlined /> Thumbnail Library</Title>
        {canUpload && (
          <Space wrap>
            <Text type="secondary">Upload to:</Text>
            <Select value={uploadCategory} onChange={setUploadCategory} style={{ width: 190 }}>
              {CATEGORIES.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
            </Select>
            <DatePicker value={uploadDate} onChange={(d) => setUploadDate(d || dayjs())} allowClear={false} format="YYYY-MM-DD" style={{ width: 150 }} />
            <Upload
              multiple
              accept="image/*"
              showUploadList={false}
              beforeUpload={() => false}
              onChange={onUploadChange}
              fileList={[]}
            >
              <Button icon={<UploadOutlined />} type="primary">Bulk Upload Thumbnails</Button>
            </Upload>
            {(user?.role === 'admin' || user?.role === 'super_admin') && (
              <>
                <Text type="secondary" style={{ marginLeft: 8 }}>Quick Delete:</Text>
                <Popconfirm title={`Delete all ${uploadCategory} uploads from today?`} onConfirm={() => deleteDateRange(0)} okText="Yes" okButtonProps={{ danger: true }}>
                  <Button danger size="small">Today</Button>
                </Popconfirm>
                <Popconfirm title={`Delete all ${uploadCategory} uploads from last 2 days?`} onConfirm={() => deleteDateRange(1)} okText="Yes" okButtonProps={{ danger: true }}>
                  <Button danger size="small">2 Days</Button>
                </Popconfirm>
                <Popconfirm title={`Delete all ${uploadCategory} uploads from last 3 days?`} onConfirm={() => deleteDateRange(2)} okText="Yes" okButtonProps={{ danger: true }}>
                  <Button danger size="small">3 Days</Button>
                </Popconfirm>
              </>
            )}
          </Space>
        )}
      </Space>

      <div style={{ marginBottom: 12, overflowX: 'auto' }}>
        <Segmented
          value={catFilter || 'all'}
          onChange={(v) => { const cat = v === 'all' ? '' : v; setCatFilter(cat); setPage(1); if (cat) setUploadCategory(cat); }}
          options={[{ label: 'All', value: 'all' }, ...CATEGORIES.map(c => ({ label: c.label, value: c.value }))]}
        />
      </div>

      <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by name (e.g. kabutar bole)..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          allowClear
          style={{ width: 320 }}
        />
        <RangePicker value={dateRange} onChange={(v) => { setDateRange(v); setPage(1); }} format="YYYY-MM-DD" placeholder={['From date', 'To date']} />
        <Button icon={<CalendarOutlined />} onClick={setThisWeek}>This week (Mon-Sat)</Button>
        {canDownload && (fromStr && toStr) && (
          <Button icon={<DownloadOutlined />} type="primary" ghost onClick={downloadWeek}>Download Week (ZIP)</Button>
        )}
        {canDownload && items.length > 0 && (
          <Checkbox checked={allShownSelected} onChange={e => toggleSelectAll(e.target.checked)}>Select all ({items.length})</Checkbox>
        )}
        {canDownload && (
          <>
            <Button icon={<DownloadOutlined />} type="primary" disabled={selected.length === 0} onClick={downloadSelected}>
              Download Selected ({selected.length})
            </Button>
            <Button icon={<DownloadOutlined />} onClick={downloadAllShown} disabled={items.length === 0}>Download All Shown (ZIP)</Button>
          </>
        )}
        {canUpload && (
          <Popconfirm
            title={`Delete ${selected.length} selected thumbnail(s)?`}
            onConfirm={deleteSelected}
            okText="Yes, delete"
            cancelText="No"
            disabled={selected.length === 0}
          >
            <Button icon={<DeleteOutlined />} danger disabled={selected.length === 0}>
              Delete Selected ({selected.length})
            </Button>
          </Popconfirm>
        )}
        {(user?.role === 'admin' || user?.role === 'super_admin') && total > 0 && (
          <Popconfirm
            title={`Delete ALL ${total} thumbnail(s)? This cannot be undone!`}
            onConfirm={deleteAll}
            okText="Yes, delete all"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button icon={<DeleteOutlined />} danger type="primary">
              Delete All ({total})
            </Button>
          </Popconfirm>
        )}
      </Space>



      {total > 0 && (
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Text type="secondary">{total.toLocaleString()} thumbnail(s){search || catFilter ? ' found' : ''}</Text>
          <Pagination
            current={page}
            total={total}
            pageSize={PAGE_SIZE}
            onChange={(p) => { setPage(p); setSelected([]); }}
            showSizeChanger={false}
            showQuickJumper={total > PAGE_SIZE * 5}
            simple={total <= PAGE_SIZE * 3}
          />
        </div>
      )}

      {items.length > 0 ? (
        <Row gutter={[16, 16]}>
          {items.map(t => {
            const meta = catMeta(t.category);
            const isSel = selected.includes(t.id);
            return (
              <Col xs={12} sm={8} md={6} lg={4} key={t.id}>
                <Card
                  hoverable
                  loading={loading}
                  style={{ border: isSel ? '2px solid #667eea' : undefined }}
                  styles={{ body: { padding: 12 } }}
                  cover={
                    <div style={{ position: 'relative', height: 160, overflow: 'hidden', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Checkbox
                        checked={isSel}
                        onChange={e => toggleSelect(t.id, e.target.checked)}
                        style={{ position: 'absolute', top: 8, left: 8, zIndex: 2, background: '#fff', borderRadius: 4, padding: 2 }}
                      />
                      <Image
                        src={t.preview_url ? `${API_BASE}${t.preview_url}?token=${localStorage.getItem('token')}` : `${API_BASE}${t.url}`}
                        alt={t.name}
                        loading="lazy"
                        style={{ maxHeight: 160, objectFit: 'contain' }}
                        preview={{ mask: 'Preview', src: t.preview_url ? `${API_BASE}${t.preview_url}?size=lg&token=${localStorage.getItem('token')}` : `${API_BASE}${t.url}` }}
                      />
                    </div>
                  }
                  actions={[
                    ...(canDownload ? [<DownloadOutlined key="dl" onClick={() => downloadOne(t.id)} title="Download" />] : []),
                    ...(canUpload ? [
                      <EditOutlined key="edit" onClick={() => { setEditingThumbId(t.id); setEditThumbTitle(t.name || t.title || ''); }} title="Edit Title" />,
                      <Popconfirm key="del" title="Delete this thumbnail?" onConfirm={() => deleteOne(t.id)} okText="Yes" cancelText="No">
                        <DeleteOutlined style={{ color: '#ff4d4f' }} title="Delete" />
                      </Popconfirm>
                    ] : [])
                  ]}
                >
                  <Card.Meta
                    title={editingThumbId === t.id ? (
                      <Input size="small" value={editThumbTitle} onChange={e => setEditThumbTitle(e.target.value)}
                        onPressEnter={() => handleThumbTitleEdit(t.id)} onBlur={() => handleThumbTitleEdit(t.id)} autoFocus />
                    ) : (
                      <Text strong style={{ fontSize: 13 }} ellipsis={{ tooltip: t.name }}>{t.name}</Text>
                    )}
                    description={<><Tag color={meta.color} style={{ marginTop: 4 }}>{meta.label}</Tag>{t.event_date && <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>{t.event_date}</Text>}</>}
                  />
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <Empty description={search || catFilter ? 'No thumbnails found' : 'No thumbnails uploaded yet'} style={{ marginTop: 60 }} />
      )}

      {total > PAGE_SIZE && (
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <Pagination
            current={page}
            total={total}
            pageSize={PAGE_SIZE}
            onChange={(p) => { setPage(p); setSelected([]); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            showSizeChanger={false}
            showQuickJumper={total > PAGE_SIZE * 5}
          />
        </div>
      )}
    </div>
  );
}
